import { useState } from "react";
import { Check, MapPin } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, ClipImage } from "@/components/Motion";
import { CallButton, ConsultButton, TextLink } from "@/components/Buttons";
import { ServiceAreaMap } from "@/components/ServiceAreaMap";
import { usePageMeta } from "@/components/Common";
import { COMMUNITIES, PHONE, AREAS_TEXT, TEL } from "@/data/site";

const HomeBase = ({ c }) => (
  <article id={c.slug} data-testid={`area-${c.slug}`} className="scroll-mt-28 py-16 md:py-24">
    <div className="grain relative overflow-hidden rounded-[4px] bg-[#14263D] text-white">
      <ClipImage src={c.img} alt={c.alt} className="h-72 md:h-[520px]" />
      <div className="relative p-8 md:p-14 lg:grid lg:grid-cols-12 lg:gap-12">
        <Reveal className="lg:col-span-7">
          <span data-testid="home-base-badge" className="inline-flex items-center gap-2 rounded-full bg-[#A52A32] px-4 py-2 font-mono text-xs uppercase tracking-[0.2em]"><MapPin className="h-3.5 w-3.5" />Home Base</span>
          <h2 className="mt-6 font-display text-5xl font-semibold leading-[0.98] sm:text-6xl lg:text-7xl">{c.name}<span className="text-[#f0b3b7]">,</span> <span className="italic">Florida</span></h2>
          <p className="mt-6 text-lg font-medium leading-relaxed text-white">{c.lead}</p>
          <p className="mt-4 text-base leading-relaxed text-white/85 md:text-lg">{c.body}</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-10 lg:col-span-4 lg:col-start-9 lg:mt-0">
          <ul className="space-y-3">
            {c.points.map((p) => (
              <li key={p} className="flex items-center gap-3 border-b border-white/15 pb-3 text-base"><Check className="h-4 w-4 text-[#f0b3b7]" />{p}</li>
            ))}
          </ul>
          <p className="mt-6 font-display text-3xl">Personalized quotes <span className="text-base text-white/80">after a free consultation</span></p>
          <div className="mt-8"><TextLink light to={`/service-areas/${c.slug}`} testId={`area-page-link-${c.slug}`}>{`Home watch in ${c.name}`}</TextLink></div>
        </Reveal>
      </div>
    </div>
  </article>
);

const Community = ({ c, i }) => (
  <article id={c.slug} data-testid={`area-${c.slug}`} className="scroll-mt-28 grid gap-10 border-t border-[#14263D]/15 py-16 md:py-24 lg:grid-cols-12 lg:items-center">
    <div className={`lg:col-span-5 ${i % 2 ? "lg:order-2 lg:col-start-8" : ""}`}>
      <ClipImage src={c.img} alt={c.alt} className="h-72 rounded-[4px] md:h-[440px]" />
    </div>
    <div className={`lg:col-span-6 ${i % 2 ? "lg:order-1" : "lg:col-start-7"}`}>
      <Reveal>
        <p className="eyebrow">0{i + 1} · Home Watch {c.name}, Florida</p>
        <h2 className="mt-4 font-display text-5xl font-semibold text-[#14263D] md:text-6xl">{c.name}<span className="text-[#A52A32]">,</span> <span className="italic">Florida</span></h2>
        <p className="mt-6 text-lg font-medium leading-relaxed text-[#14263D]">{c.lead}</p>
        <p className="mt-4 text-base leading-relaxed text-[#5F6B76]">{c.body}</p>
        <ul className="mt-6 flex flex-wrap gap-2">
          {c.points.map((p) => (
            <li key={p} className="flex items-center gap-2 rounded-full bg-[#F8F7F3] px-4 py-2 text-sm text-[#14263D]"><Check className="h-3.5 w-3.5 text-[#A52A32]" />{p}</li>
          ))}
        </ul>
        <div className="mt-8"><TextLink to={`/service-areas/${c.slug}`} testId={`area-page-link-${c.slug}`}>{`More about home watch in ${c.name}`}</TextLink></div>
      </Reveal>
    </div>
  </article>
);

export default function ServiceAreas() {
  const [active, setActive] = useState(null);
  usePageMeta("Home Watch Wellington, Royal Palm Beach, Westlake, Arden & Loxahatchee FL | First Due", `Home watch & concierge for ${AREAS_TEXT.replace(" and ", " & ")}, FL. Call 561-722-3224.`);
  const select = (name) => {
    setActive(name);
    const el = document.getElementById(name.toLowerCase().replace(/\s/g, "-"));
    el?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <main data-testid="service-areas-page">
      <PageHero testId="areas-hero" eyebrow="Service Areas" lines={["Locally Owned.", "Locally Focused."]} intro="First Due Home Watch & Concierge proudly serves western Palm Beach County." img="/images/avenue.jpg" alt="Avenue of royal palm trees" />
      <section className="bg-[#F8F7F3] pt-20 md:pt-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal>
            <p className="eyebrow">Primary Service Area</p>
            <p data-testid="primary-service-area-text" className="mt-4 max-w-4xl font-display text-3xl leading-snug text-[#14263D] md:text-4xl">
              First Due Home Watch &amp; Concierge proudly provides professional home watch and concierge services throughout western Palm Beach County, including {AREAS_TEXT}.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="bg-[#F8F7F3] py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-10 lg:grid-cols-12 lg:items-center">
          <Reveal className="lg:col-span-7"><ServiceAreaMap active={active} onSelect={select} /></Reveal>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="eyebrow">Jump to a community</p>
            <ul className="mt-6">
              {COMMUNITIES.map((c, i) => (
                <li key={c.slug}>
                  <button
                    type="button"
                    data-testid={`area-jump-${c.slug}`}
                    onClick={() => select(c.name)}
                    onMouseEnter={() => setActive(c.name)}
                    className="group flex w-full items-baseline justify-between border-b border-[#14263D]/15 py-4 text-left"
                  >
                    <span className="font-display text-3xl text-[#14263D] transition-transform duration-300 group-hover:translate-x-2">{c.name}</span>
                    <span className="font-mono text-xs text-[#A52A32]">{i === 0 ? "Home Base" : `0${i + 1}`}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <HomeBase c={COMMUNITIES[0]} />
          {COMMUNITIES.slice(1).map((c, i) => <Community key={c.slug} c={c} i={i + 1} />)}
        </div>
      </section>
      <section data-testid="area-unsure" className="grain relative overflow-hidden bg-[#14263D] py-24 text-white md:py-32">
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal>
            <h2 className="max-w-4xl font-display text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">Outside Our Primary Service Area?</h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg">We may be able to accommodate homeowners in other Palm Beach County communities on a case-by-case basis, depending on location, scheduling and service requirements.</p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg">Contact us at <a href={TEL} data-testid="areas-outside-phone" className="font-semibold text-white underline underline-offset-4">{PHONE}</a> to discuss your property.</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <CallButton testId="areas-call-btn" dark label={`Call ${PHONE}`} className="!bg-[#A52A32] !border-[#A52A32]" />
              <ConsultButton testId="areas-consult-btn" label="Schedule a Free Consultation" dark className="!bg-transparent border border-white/40" />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
